import { useState } from 'react'
import { Alert, App, Button, Drawer, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { RowActions } from '@/shared/ui/table/RowActions'
import { formatDateTime } from '@/shared/lib/format'
import { downloadCsv } from '@/shared/lib/download'
import { useCanonicalPlanIndex } from '@/shared/api/reference'
import { shortId } from '@/shared/lib/shortId'
import { voucherActivator, voucherSeller, type VoucherActor } from './voucherActor'
import { VoucherActivateModal } from './VoucherActivateModal'
import { VoucherPrintSheet } from './VoucherPrintSheet'
import { serialLabel } from './voucherPrintLayout'
import { VoucherVoidModal } from './VoucherVoidModal'
import {
  fetchVoucherCodes,
  markVoucherSold,
  unsellVoucher,
  type Voucher,
  type VoucherBatch,
  type VoucherState,
} from './voucherApi'

interface VoucherCodesDrawerProps {
  batch: VoucherBatch | null
  onClose: () => void
}

const STATE_COLOR: Record<VoucherState, string> = {
  NEW: 'blue',
  SOLD: 'gold',
  ACTIVATED: 'green',
  EXPIRED: 'default',
  VOIDED: 'red',
}

/**
 * Qaysi holatdagi kartani bekor qilish MUMKIN — backenddagi shart bilan
 * AYNAN bir xil (`VoucherServiceImpl.voidVoucher`: `NEW` va `SOLD` dan
 * boshqasi 409 bilan rad etiladi).
 *
 * ⚠️ `ACTIVATED` bu yerda ATAYLAB yo'q: karta allaqachon grant bergan va
 * xizmat ko'rsatilgan, uni bekor qilish hech narsani qaytarmaydi.
 * `EXPIRED`/`VOIDED` esa allaqachon o'lik — ular uchun tugma ko'rsatish
 * "bosdim, xato chiqdi" degan bo'sh yo'l bo'lardi.
 */
const VOIDABLE: readonly VoucherState[] = ['NEW', 'SOLD']

/**
 * Qaysi holatdagi kartani ISHLATISH mumkin — backenddagi shart bilan
 * AYNAN bir xil (`Voucher.isActivatable()`: `NEW` va `SOLD`).
 *
 * ⚠️ {@link VOIDABLE} bilan bir xil ro'yxat, lekin ALOHIDA doimiy: bular
 * ikki BOSHQA qoida (biri kartani o'ldiradi, ikkinchisi ishlatadi) va
 * ularni bitta o'zgaruvchiga qo'shish bittasi o'zgarganda ikkinchisini
 * jimgina birga o'zgartirardi.
 */
const ACTIVATABLE: readonly VoucherState[] = ['NEW', 'SOLD']

/**
 * Aktyor katakchasi — TO'RT holat, TO'RT xil ko'rinish.
 *
 * ⚠️ ENG MUHIMI ikkinchisi: «hodisa bo'lgan, aktyor yozilmagan».
 * `voucher.activated_by` ustuni bazaga KEYIN qo'shiladi, ya'ni eski
 * kartalarda u bo'sh bo'ladi. Chiziqcha bilan chizilsa, ishlatilgan
 * karta «hech kim ishlatmagan» bo'lib ko'rinardi — sabab
 * `voucherActor.ts` da.
 *
 * ⚠️ Xom UUID chizilmaydi: nom topilmasa qisqartirilgan shakl
 * (`shortId` — B4/B5 dagi bir xil funksiya) va nusxa olish uchun
 * to'liq qiymat.
 */
function VoucherActorCell({ actor }: { actor: VoucherActor }) {
  const { t } = useTranslation()

  if (actor.kind === 'none') {
    return <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
  }

  if (actor.kind === 'unrecorded') {
    return (
      <Tooltip title={t('voucher.actorUnrecordedHint')}>
        <Tag>{t('voucher.actorUnrecorded')}</Tag>
      </Tooltip>
    )
  }

  if (actor.kind === 'id') {
    return (
      <Tooltip title={t('voucher.actorIdOnlyHint', { id: actor.id })}>
        <Typography.Text code copyable={{ text: actor.id }} style={{ fontSize: 12 }}>
          {shortId(actor.id)}
        </Typography.Text>
      </Tooltip>
    )
  }

  return <Typography.Text>{actor.name}</Typography.Text>
}

/**
 * Kartalarni ochib beradi — CHOP ETISH/EKSPORT UCHUN.
 *
 * ⚠️ BIR MARTALIK SIR (`voucherApi.ts` izohi): bu ekran ochilganda so'raladi
 * (`enabled: batch !== null`), yopilganda esa Drawer `destroyOnHidden` bilan
 * DOM'dan olib tashlanadi — batch ro'yxatining o'zida kodlar hech qachon
 * ko'rinmaydi, faqat shu aniq amal orqali.
 *
 * ⚠️ BITTA karta ustidagi amallar ham shu yerda ("sotildi", uni qaytarish,
 * bekor qilish) va bu ataylab: kartani AJRATIB olishning yagona joyi shu
 * ro'yxat — alohida "kartalar" sahifasi bo'lsa, u kodlarni ikkinchi marta
 * ochib beradigan ekran bo'lardi. Har amalning huquqi va mumkin bo'lgan
 * holati pastda, amallar ustunida.
 */
export function VoucherCodesDrawer({ batch, onClose }: VoucherCodesDrawerProps) {
  const { t } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const [voidTarget, setVoidTarget] = useState<Voucher | null>(null)
  /** Ishlatish oynasiga oldindan uzatiladigan kod — `null` = oyna yopiq */
  const [activateCode, setActivateCode] = useState<string | null>(null)
  const [printOpen, setPrintOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const planNames = useCanonicalPlanIndex()
  /*
    ⚠️ Sahifalash BOSHQARILADIGAN bo'ldi: tartib raqami sahifaga mos
    bo'lishi kerak (2-sahifada 51, 52, …), antd esa boshqarilmagan
    rejimda joriy sahifani tashqariga bermaydi.
  */
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const codes = useQuery({
    queryKey: queryKeys.vouchers.codes(batch?.id ?? ''),
    queryFn: () => fetchVoucherCodes(batch?.id ?? '', batch?.quantity ?? 1000),
    enabled: batch !== null,
  })

  /*
    ⚠️ Karta holati o'zgarganda IKKI kalit eskiradi: shu ro'yxat va
    partiyalar jadvalidagi sanoq (`VoucherBatch.counts`). Ikkinchisini
    unutish oson va u JIMGINA noto'g'ri ishlaydi — drawer yopilgach
    sahifada eski "Chop etilgan: 25" qolib ketardi.
  */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.codes(batch?.id ?? '') })
    void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
  }

  const cardLabel = (row: Voucher): string =>
    batch && row.serialNo !== null ? serialLabel(batch.batchNo, row.serialNo, batch.quantity) : t('common.dash')

  const markSold = useMutation({
    mutationFn: (id: string) => markVoucherSold(id),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const unsell = useMutation({
    mutationFn: (id: string) => unsellVoucher(id),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<Voucher> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => (
        <Typography.Text type="secondary">{(page - 1) * pageSize + index + 1}</Typography.Text>
      ),
    },
    {
      key: 'card',
      title: t('voucher.serial'),
      width: 110,
      render: (_, row) => <Typography.Text>{cardLabel(row)}</Typography.Text>,
    },
    {
      key: 'code',
      title: t('voucher.code'),
      width: 200,
      render: (_, row) => (
        <Typography.Text code copyable>
          {row.code}
        </Typography.Text>
      ),
    },
    {
      /*
        ⚠️ HOLAT NOMI YOLG'IZ YETMAYDI — izohi ham kerak.

        «Chop etilgan» va «Aktivlashtirilgan» farqini kod bilan
        tanishmagan odam ekrandan TOPA OLMAYDI: birinchisi «ombordagi
        qiymat», ikkinchisi «ishlatilgan». Tashqi tekshiruvchi aynan shu
        ikkalasini ajrata olmadi.

        Izoh AYNAN teg ustida (`Tooltip`), alohida ustunda emas: ustun
        beshta holat uchun beshta jumlani har qatorda takrorlardi.
      */
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) => (
        <Tooltip title={t(`voucherStateHint.${row.state}`)}>
          <Tag color={STATE_COLOR[row.state]}>{t(`voucherState.${row.state}`)}</Tag>
        </Tooltip>
      ),
    },
    {
      key: 'soldAt',
      title: t('voucher.soldAt'),
      width: 160,
      render: (_, row) => (row.soldAt ? formatDateTime(row.soldAt) : '—'),
    },
    {
      /*
        «Kim sotdi» — `soldAt` YONIDA, chunki ular bitta hodisaning
        ikki yarmi. Sana bor, ism yo'q bo'lsa katakcha buni ochiq
        aytadi ("noma'lum (eski yozuv)"), chiziqcha CHIZMAYDI.
      */
      key: 'soldBy',
      title: t('voucher.soldBy'),
      width: 170,
      render: (_, row) => <VoucherActorCell actor={voucherSeller(row)} />,
    },
    {
      key: 'activatedAt',
      title: t('voucher.activatedAt'),
      width: 160,
      render: (_, row) => (row.activatedAt ? formatDateTime(row.activatedAt) : '—'),
    },
    {
      /*
        ⚠️ «KIM ISHLATDI» — egasi aynan shu savol bilan keldi.

        Bu ustun partiya EGASIDAN (`voucher_batch.agent_id`) chiqmaydi:
        aktivatsiyada partiya egasi UMUMAN tekshirilmaydi
        (`VoucherServiceImpl.activate`), ya'ni kartani boshqa agent yoki
        reseller ishlatgan bo'lishi mumkin. Javob faqat shu yerda.
      */
      key: 'activatedBy',
      title: t('voucher.activatedBy'),
      width: 170,
      render: (_, row) => <VoucherActorCell actor={voucherActivator(row)} />,
    },
    {
      /*
        Amallar — HOLATGA qarab, faqat MUMKIN bo'lgani.

        ⚠️ Huquq har amalda ALOHIDA tekshiriladi va ular bir xil emas:
        sotuv belgisi va uni qaytarish — pul hodisasi (`payment.create`),
        bekor qilish esa kartani o'ldiradi (`subscriber.delete`). Bu oyna
        `subscriber.create` + `plan.create` bilan ochiladi, ya'ni "oynani
        ocha oldi — demak hammasi mumkin" degan xulosa NOTO'G'RI bo'lardi.

        ⚠️ Tugmani yashirish HIMOYA EMAS (`04-ROLES.md` §8) — haqiqiy
        to'siq backendda; bu yerdagisi bosib bo'lmaydigan tugmani
        ko'rsatmaslik uchun.
      */
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      // Jadval endi gorizontal siljiydi (`scroll.x`) — amallar ustuni
      // qotirilmasa aynan u ko'rinmay qolardi
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'markSold',
              label: t('voucher.markSold'),
              onClick: () => markSold.mutate(row.id),
              permission: 'payment.create',
              // ⚠️ Faqat BOSILGAN qator aylanadi: `isPending` yolg'iz
              // qolsa jadvaldagi hamma amal birdaniga yuklanayotgandek
              // ko'rinardi.
              loading: markSold.isPending && markSold.variables === row.id,
              hidden: row.state !== 'NEW',
            },
            {
              key: 'unsell',
              label: t('voucher.unsell'),
              permission: 'payment.create',
              loading: unsell.isPending && unsell.variables === row.id,
              hidden: row.state !== 'SOLD',
              onClick: () => {
                modal.confirm({
                  title: t('voucher.unsellTitle'),
                  content: t('voucher.unsellConfirm', { code: row.code }),
                  okText: t('voucher.unsell'),
                  okButtonProps: { danger: true },
                  cancelText: t('common.cancel'),
                  // ⚠️ Xato YUTILADI, chunki u `onError` da drawer
                  // bannerga tushadi. `catch` bo'lmasa antd oynani
                  // ochiq qoldirardi va banner aynan o'sha oyna ORTIDA
                  // ko'rinmay qolardi.
                  onOk: () => unsell.mutateAsync(row.id).catch(() => undefined),
                })
              },
            },
            {
              /*
                "Ishlatish" — kod ALLAQACHON shu ekranda, ya'ni uni qo'lda
                ko'chirib boshqa oynaga terish ortiqcha qadam va terish
                xatosining manbai bo'lardi.

                ⚠️ Faqat ISHLATSA BO'LADIGAN holatlarda
                (`Voucher.isActivatable()` — `NEW` va `SOLD`); qolganlarida
                server 409 beradi va amal "bosdim, xato chiqdi" degan bo'sh
                yo'lga aylanardi. Ro'yxat `VOIDABLE` bilan bir xil bo'lib
                chiqdi, lekin BOG'LIQ EMAS: bekor qilish va ishlatish ikki
                boshqa qoida va ular kelajakda ajralishi mumkin.

                ⚠️ Huquq `voucher.activate` — bu oynani ochish sharti bilan
                bir xil emas: oyna `subscriber.create` + `plan.create` bilan
                ochiladi, ya'ni "ocha oldim, demak hammasi mumkin" degan
                xulosa noto'g'ri bo'lardi (`markSold` dagi bilan bir sabab).
              */
              key: 'activate',
              label: t('voucher.activate'),
              onClick: () => setActivateCode(row.code),
              permission: 'voucher.activate',
              hidden: !ACTIVATABLE.includes(row.state),
            },
            {
              key: 'void',
              label: t('voucher.void'),
              onClick: () => setVoidTarget(row),
              permission: 'subscriber.delete',
              danger: true,
              divider: true,
              hidden: !VOIDABLE.includes(row.state),
            },
          ]}
        />
      ),
    },
  ]

  /*
    ⚠️ EKSPORTDAGI HOLAT — EKRANDAGI BILAN AYNAN BIR XIL MATN.

    Ilgari bu yerda `row.state` turardi, ya'ni XOM enum (`NEW`), jadvalda
    esa tarjimasi (`Chop etilgan` / `Printed` / `Напечатана`). Tashqi
    tekshiruvchi aynan shuni topdi: «UI da Printed, eksportda New».

    Oqibati kosmetik emas: eksport fayli boshqa tizimga va boshqa odamga
    ketadi, va u yerda «New» kartani HALI CHOP ETILMAGAN deb o'qish
    mumkin — holbuki `NEW` bu domenda «chop etilgan, hali sotilmagan»
    degani. Ikki nom bitta holatga — bu ma'lumot ziddiyati.

    ⚠️ `t(...)` shabloni EMAS, `voucherState.${row.state}` — kalit
    ro'yxati `VOUCHER_STATES` bilan bir xil va i18n qorovuli aynan shu
    shaklni skanerlaydi (`tester-round2.mjs` B8).
  */
  const handleExport = () => {
    if (!codes.data) return
    downloadCsv(`voucher-codes-${batch?.batchNo ?? ''}`, codes.data, [
      { header: t('voucher.serial'), value: (row) => cardLabel(row) },
      { header: t('voucher.code'), value: (row) => row.code },
      { header: t('subscribers.columns.status'), value: (row) => t(`voucherState.${row.state}`) },
    ])
  }

  return (
    <Drawer
      open={batch !== null}
      title={t('voucher.codesTitle')}
      onClose={onClose}
      width={640}
      destroyOnHidden
      extra={
        <Space>
          {/*
            ⚠️ CHOP ETISH va CSV — IKKI XIL ISH, shuning uchun ikki tugma.
            CSV boshqa tizimga uzatish uchun (kod + holat, qatorlar),
            chop etish esa QOG'OZGA chiqadigan KARTA: QR, paket nomi,
            narxi va muddati. Bittasi ikkinchisining o'rnini bosmaydi —
            CSV'ni bosib chiqarsangiz sotib bo'lmaydigan jadval chiqadi.
          */}
          <Button
            icon={<PrinterOutlined />}
            disabled={!codes.data || codes.data.length === 0}
            onClick={() => setPrintOpen(true)}
          >
            {t('voucher.print.print')}
          </Button>
          <Button icon={<DownloadOutlined />} disabled={!codes.data || codes.data.length === 0} onClick={handleExport}>
            {t('dataTable.export')}
          </Button>
        </Space>
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('voucher.codesWarningTitle')}
        description={t('voucher.codesWarningText')}
      />

      {/* Amal xatosi — ro'yxatni YUKLASH xatosidan alohida: birinchisi
          jadvalni umuman ko'rsatmaydi, bu esa ustiga qo'shiladi */}
      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {codes.isError ? (
        <Alert type="error" showIcon message={translateError(codes.error, t)} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Table<Voucher>
            columns={columns}
            dataSource={codes.data ?? []}
            rowKey={(row) => row.id}
            loading={codes.isPending}
            size="small"
            // 56 + 110 + 200 + 120 + 160 + 170 + 160 + 170 + 56 — ustunlar yig'indisi
            scroll={{ x: 1202 }}
            pagination={{
              current: page,
              pageSize,
              showSizeChanger: true,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage)
                setPageSize(nextSize)
              },
            }}
          />
        </Space>
      )}

      <VoucherVoidModal voucher={voidTarget} onClose={() => setVoidTarget(null)} />

      {/* ⚠️ Kod OLDINDAN to'ldiriladi — ro'yxatdan ochilganda uni qayta
          terish terish xatosining bekorga qo'shilgan manbai bo'lardi */}
      <VoucherActivateModal
        open={activateCode !== null}
        code={activateCode}
        onClose={() => setActivateCode(null)}
      />

      {/* Varaq kodlarni o'zi so'raydi: chop etish serverda auditga yozilishi va
          portal manzili shu javob bilan kelishi uchun. */}
      <VoucherPrintSheet
        batch={printOpen ? batch : null}
        planName={batch ? (planNames.get(batch.planId) ?? null) : null}
        onClose={() => setPrintOpen(false)}
      />
    </Drawer>
  )
}
